import React, { useState, useRef, useEffect } from 'react';
import { addDays, format } from 'date-fns';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { useTranslation } from 'react-i18next';
import { useGetAllOrder } from '../hooks/hooks';
import Skeleton from 'react-loading-skeleton';
import moment from 'moment';

function OrderReport() {
   const { t } = useTranslation();
   const [selectedStatus, setSelectedStatus] = useState(0);
   const [selectedRange, setSelectedRange] = useState([
      {
         startDate: new Date(),
         endDate: addDays(new Date(), 7),
         key: 'selection',
      },
   ]);
   const [tempRange, setTempRange] = useState(selectedRange);
   const [showPicker, setShowPicker] = useState(false);
   const pickerRef = useRef(null);

   useEffect(() => {
      const handleClickOutside = (event) => {
         if (pickerRef.current && !pickerRef.current.contains(event.target)) {
            setShowPicker(false);
            setTempRange(selectedRange); // Reset if canceled
         }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
   }, [selectedRange]);

   const handleApply = () => {
      setSelectedRange(tempRange);
      setShowPicker(false);
   };

   const handleClose = () => {
      setTempRange(selectedRange);
      setShowPicker(false);
   };

   const formattedRange = `${format(selectedRange[0].startDate, 'MMM d, yyyy')} - ${format(selectedRange[0].endDate, 'MMM d, yyyy')}`;
   
   const { data: orderList, isLoading: IsLoading } = useGetAllOrder({
      startDate: selectedRange[0].startDate,
      endDate: selectedRange[0].endDate,
      selectedStatus: selectedStatus,
      searchText: ''
   });

   return (
      <div className='mt-7'>
         {/* Enhanced Filters Section */}
         <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
               <h3 className="text-lg font-semibold text-gray-800">{t('Order_Report_Filters')}</h3>
               <div className="flex items-center gap-2 text-sm text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
                  </svg>
                  <span>{t('Filter_orders_by_date_range_and_status')}</span>
               </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
               {/* Date Range Picker */}
               <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                     <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                     </svg>
                     {t('Date_Range')}
                  </label>
                  <div className="relative">
                     <div
                        onClick={(e) => {
                           e.stopPropagation();
                           setShowPicker((prev) => !prev);
                        }}
                        className="flex items-center justify-between w-full px-4 py-3 border border-gray-300 rounded-lg bg-white hover:border-blue-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 cursor-pointer"
                     >
                        <div className="flex items-center">
                           <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                           </svg>
                           <span className={formattedRange ? "text-gray-900" : "text-gray-500"}>
                              {formattedRange || t('Select_date_range')}
                           </span>
                        </div>
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                     </div>
                     {showPicker && (
                        <div
                           ref={pickerRef}
                           className="absolute left-0 z-50 bg-white shadow-xl p-6 rounded-lg mt-2 border border-gray-200"
                        >
                           <div className="w-auto overflow-hidden">
                              <DateRangePicker
                                 onChange={(item) => setTempRange([item.selection])}
                                 months={2}
                                 ranges={tempRange}
                                 direction="horizontal"
                                 showSelectionPreview={true}
                                 moveRangeOnFirstSelection={false}
                                 preventSnapRefocus={true}
                                 calendarFocus="backwards"
                              />
                              <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-200">
                                 <button
                                    onClick={(e) => {
                                       e.stopPropagation();
                                       handleClose();
                                    }}
                                    className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
                                 >
                                    {t('Cancel')}
                                 </button>
                                 <button
                                    onClick={handleApply}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
                                 >
                                    {t('Apply_Filter')}
                                 </button>
                              </div>
                           </div>
                        </div>
                     )}
                  </div>
               </div>

               {/* Order Status Filter */}
               <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                     <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                     </svg>
                     {t('Order_Status')}
                  </label>
                  <div className="relative">
                     <select
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(parseInt(e.target.value))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white appearance-none cursor-pointer"
                     >
                        <option value={0}>All Orders</option>
                        <option value={1}>In-progress</option>
                        <option value={2}>Completed</option>
                        <option value={3}>Upcoming</option>
                        <option value={4}>Cancelled</option>
                     </select>
                     <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                     </div>
                  </div>
               </div>
            </div>
            
            {/* Filter Summary */}
            <div className="mt-4 pt-4 border-t border-gray-200">
               <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                     <span className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span>{t('Date')}: {formattedRange}</span>
                     </span>
                     <span className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span>{t('Status')}: {
                           selectedStatus === 0 ? 'All Orders' :
                           selectedStatus === 1 ? 'In Progress' :
                           selectedStatus === 2 ? 'Completed' :
                           selectedStatus === 3 ? 'Upcoming' :
                           selectedStatus === 4 ? 'Cancelled'  : 'All Orders'
                        }</span>
                     </span>
                  </div>
                  <div className="text-sm text-gray-500">
                     {orderList && orderList.length > 0 ? `${orderList.length} ${t('orders_found')}` : t('No_orders_found')}
                  </div>
               </div>
            </div>
         </div>

         {/* Enhanced Table */}
         <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Table Header */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
               <div className="grid grid-cols-12 gap-4 px-6 py-4">
                  <div className="col-span-1 text-sm font-semibold text-gray-700 flex items-center">
                     <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                     </svg>
                     
                  </div>
                  <div className="col-span-2 text-sm font-semibold text-gray-700 flex items-center">
                     <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                     </svg>
                     {t('Order_Id')}
                  </div>
                  <div className="col-span-2 text-sm font-semibold text-gray-700 flex items-center">
                     <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                     </svg>
                     {t('Party_Name')}
                  </div>
                  <div className="col-span-2 text-sm font-semibold text-gray-700 flex items-center">
                     <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                     </svg>
                     {t('Contact_Number')}
                  </div>
                  <div className="col-span-1 text-sm font-semibold text-gray-700 flex items-center">
                     <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                     </svg>
                     {t('Status')}
                  </div>

                  <div className="col-span-1 text-sm font-semibold text-gray-700 flex items-center">
                     <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                     </svg>
                     {t('Date')}
                  </div>

                  <div className="col-span-1 text-sm font-semibold text-gray-700 flex items-center">
                     <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                     </svg>
                     {t('Total_Amount')}
                  </div>
                  <div className="col-span-1 text-sm font-semibold text-gray-700 flex items-center">
                     <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                     </svg>
                     {t('Paid')}
                  </div>
                  <div className="col-span-1 text-sm font-semibold text-gray-700 flex items-center">
                     <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                     </svg>
                     {t('due_amount')}
                  </div>
               </div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-gray-200">
               {IsLoading ? (
                  <>
                     {Array.from({ length: 8 }, (_, index) => (
                        <div className="grid grid-cols-11 gap-4 px-6 py-4" key={index}>
                           <div className="col-span-11">
                              <Skeleton height={40} />
                           </div>
                        </div>
                     ))}
                  </>
               ) : (
                  <>
                     {orderList && orderList.length > 0 ? (
                        orderList.map((item, index) => (
                           <div
                              key={index}
                              className="grid grid-cols-12 gap-4 px-6 py-4 hover:bg-gray-50 transition-colors duration-200 group"
                           >
                              <div className="col-span-1 flex items-center">
                                 <span className="text-sm font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                                    {index + 1}
                                 </span>
                              </div>
                              <div className="col-span-2 flex items-center">
                                 <div className="flex items-center">
                                    <span className="text-sm font-medium text-gray-900 truncate">
                                       {item.order_id}
                                    </span>
                                 </div>
                              </div>
                              <div className="col-span-2 flex items-center">
                                 <div className="flex items-center">
                                    <span className="text-sm text-gray-900 truncate">
                                       {item.partyName}
                                    </span>
                                 </div>
                              </div>
                              <div className="col-span-2 flex items-center">
                                 <div className="flex items-center">
                                    <span className="text-sm text-gray-900">
                                       {item.contactNumber}
                                    </span>
                                 </div>
                              </div>
                              <div className="col-span-1 flex items-center">
                                 <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                    item?.status === 1 ? ' bg-red-100 text-red-800 border border-red-200 ' :
                                    item?.status === 2 ? 'bg-green-100 text-green-800 border border-green-200 ' :
                                    item?.status === 3 ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                                    item?.status === 4 ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                                    'bg-gray-100 text-gray-800 border border-gray-200'
                                 }`}>
                                    {item?.status === 1 ? 'In-progress' :
                                     item?.status === 2 ? 'Completed' :
                                     item?.status === 3 ? 'Upcoming' :
                                     item?.status === 4 ? 'Cancelled' :
                                     'N/A'}
                                 </span>
                              </div>
                             <div className="col-span-1 flex items-center">
                                 <div className="flex items-center">
                                    <span className="text-sm text-sm text-gray-900">
                                       {moment(item.eventDate).format('DD-MM-YYYY')}
                                    </span>
                                 </div>
                              </div>
                              <div className="col-span-1 flex items-center">
                                 <div className="flex items-center">
                                    <span className="text-sm font-semibold text-gray-900">
                                       ₹{item?.OrderDetails?.[0]?.totalAmount ?? '0'}
                                    </span>
                                 </div>
                              </div>
                              <div className="col-span-1 flex items-center">
                                 <div className="flex items-center">
                                    <span className="text-sm font-semibold text-green-600">
                                       ₹{item?.OrderDetails?.[0]?.paidByClient ?? '0'}
                                    </span>
                                 </div>
                              </div>
                              <div className="col-span-1 flex items-center">
                                 <div className="flex items-center">
                                    <span className="text-sm font-semibold text-red-600">
                                       ₹{item?.OrderDetails?.[0]?.dueAmount ?? '0'}
                                    </span>
                                 </div>
                              </div>
                           </div>
                        ))
                     ) : (
                        <div className="px-6 py-16 text-center">
                           <div className="flex flex-col items-center">
                              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                 <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                 </svg>
                              </div>
                              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('No_orders_found')}</h3>
                              <p className="text-gray-500 max-w-sm">
                                 {t('No_orders_match_filter_criteria')}
                              </p>
                           </div>
                        </div>
                     )}
                  </>
               )}
            </div>
         </div>
      </div>
   );
}

export default OrderReport;
